// components/entities/OrganisationRenderer.tsx
import React, { useCallback, useState } from 'react';
import { useKernel } from '@/kernel/Kernel';
import { useKernelStore } from '@/kernel/kernelStores';
import type { BaseEntityProps, EntityRowAction, LeasingOrganisationDTO, QuickMenuAction } from '#types/types';
import { Building } from '@/components/ui/lucide-memo';
import { RowMoreButton } from '@/components/ui/RowMoreButton';
import clsx from 'clsx';
import { format } from 'date-fns';
import { formatCreditsCompact, formatCreditsInteger } from '#lib/formatCredits';
import type { TableColumnDef } from '../portal/Table';
import { EntitySkeleton } from './EntityView';

export interface OrganisationRendererProps extends BaseEntityProps<LeasingOrganisationDTO> {
  onDelete?: (org: LeasingOrganisationDTO, e: React.MouseEvent) => void;
  /** Passed by EntityView's uniform call; the list row lays itself out. */
  columns?: TableColumnDef[];
  rowActions?: EntityRowAction<LeasingOrganisationDTO>[];
}

export const OrganisationRenderer = React.memo(({
  entityId,
  variant = 'row',
  onClick,
  onDelete,
  className,
}: OrganisationRendererProps) => {
  const { kernel } = useKernel();
  const data = useKernelStore((state) => state.inventory.Organisation.registry[entityId]) as LeasingOrganisationDTO | undefined;
  const [imgError, setImgError] = useState(false);
  const handleImgError = useCallback(() => setImgError(true), []);

  const handleOpen = useCallback(() => {
    if (onClick) { onClick(); return; }
    void kernel.current?.focusEntityUI('Organisation', entityId);
  }, [entityId, kernel, onClick]);

  const handleMenuClick = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    if (!data) return;
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    const actions: QuickMenuAction[] = [{ label: 'Open', onClick: handleOpen }];
    // Fater is the house; it is never offered for deletion.
    if (onDelete && data.name !== 'Fater') actions.push({ label: 'Delete', onClick: () => onDelete(data, e), danger: true, separatorBefore: true });
    kernel.current?.setMenuData({ position: { x: rect.right, y: rect.top }, actions });
  }, [data, handleOpen, kernel, onDelete]);

  if (!data) return <EntitySkeleton variant={variant} className={className} />;

  if (variant === 'chip') {
    return (
      <div className={clsx('flex items-center gap-2 px-2 py-1 rounded bg-white/5 border border-white/10 text-xs text-white/80', className)}>
        <Building size={12} />
        <span className="truncate">{data.name}</span>
      </div>
    );
  }

  // ChatGPT's list row (owner, 2026-09-29: 'the table is not necessary'): a round avatar, the name,
  // one grey line with everything the six columns used to say, the credits on the right, and the
  // row's "…" on hover. Clicking anywhere opens the organisation.
  const logoSrc = data.logoUrl && !imgError ? `/api/organisation/logo?orgId=${data.id}` : null;
  const since = (() => { try { return format(new Date(data.createdAt), 'MMM yyyy'); } catch { return null; } })();
  const details = [
    data.contactEmail,
    `${data.userCount ?? 0} ${data.userCount === 1 ? 'user' : 'users'}`,
    `${data.projectCount ?? 0} ${data.projectCount === 1 ? 'project' : 'projects'}`,
    since ? `since ${since}` : null,
  ].filter(Boolean).join(' · ');
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={handleOpen}
      onKeyDown={(e) => { if (e.key === 'Enter') handleOpen(); }}
      className={clsx('group flex cursor-pointer items-center gap-3 rounded-[12px] px-3 py-2.5 transition-colors hover:bg-[#1A1A1A] animate-card-appear', className)}
    >
      <div className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-full bg-[#1B1B1B]">
        {logoSrc
          ? <img src={logoSrc} alt="" className="h-full w-full object-contain p-1.5" onError={handleImgError} />
          : <span className="font-medium text-[#8E8E8E]" style={{ fontSize: 14 }}>{data.name.trim().charAt(0).toUpperCase()}</span>}
      </div>
      <div className="min-w-0 flex-1">
        <div className="truncate font-medium text-[#ECECEC]" style={{ fontSize: 14, lineHeight: '20px' }}>{data.name}</div>
        <div className="truncate text-[#8E8E8E]" style={{ fontSize: 13, lineHeight: '18px' }}>{details}</div>
      </div>
      <div className="shrink-0 text-[#8E8E8E] tabular-nums" style={{ fontSize: 13 }} title={`${formatCreditsCompact(data.totalCreditsSpent)} credits spent`}>
        {formatCreditsInteger(data.remainingCredits)} credits left
      </div>
      <RowMoreButton onClick={handleMenuClick} />
    </div>
  );
});

OrganisationRenderer.displayName = 'OrganisationRenderer';
